import { Button, Col, Row, Typography } from "antd";
import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { ShadowBoxCard } from "./card";

const { Title, Text } = Typography;

const badgePop = keyframes`
  0% {
    transform: scale(0.6);
    opacity: 0;
  }
  60% {
    transform: scale(1.08);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const checkDraw = keyframes`
  from {
    stroke-dashoffset: 60;
  }
  to {
    stroke-dashoffset: 0;
  }
`;

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

/**
 * Posle zakazivanja na stranici ostane samo kartica potvrde, pa je sadržaj
 * mnogo niži od ekrana: kartica visi uz vrh, a ispod nje zjapi prazan prostor
 * do futera. Sekcija zauzme tu preostalu visinu i centrira karticu u njoj.
 *
 * Oduzeta vrednost je zbir onoga što ide ispod sadržaja (futer ~400px na desktopu
 * sa tri kolone linkova + vertikalni padding `AppContent`, 48px). Ako se visina
 * futera ponovo značajno promeni, ovde treba ažurirati broj. Namerno je malo manja od stvarne: ako
 * promaši naniže kartica samo sedne nešto iznad sredine, a ako bi promašila
 * naviše stranica bi dobila suvišan skrol. Na telefonu kartica ionako popunjava
 * ekran, pa centriranje nema šta da doda.
 */
const SuccessSection = styled(Row)`
  min-height: calc(100svh - var(--header-height) - 440px);
  align-items: center;

  @media (max-width: 768px) {
    min-height: 0;
  }
`;

const SuccessWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 32px 24px 24px;
`;

const SuccessBadge = styled.div`
  width: 96px;
  height: 96px;
  border-radius: 50%;
  background: var(--color-gradient);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: var(--shadow-primary-lg);
  margin-bottom: 24px;
  animation: ${badgePop} 480ms var(--ease-out) both;

  svg {
    width: 44px;
    height: 44px;
    color: #fff;
  }

  svg path {
    stroke: #fff;
    stroke-width: 3.5;
    stroke-linecap: round;
    stroke-linejoin: round;
    fill: none;
    stroke-dasharray: 60;
    stroke-dashoffset: 60;
    animation: ${checkDraw} 360ms 240ms var(--ease-out) forwards;
  }
`;

const SuccessTitle = styled(Title)`
  &.ant-typography {
    font-size: clamp(1.125rem, 4.5vw, 1.5rem);
    margin: 0 0 8px;
    color: var(--color-text);
    /*
      Tekst dolazi iz prevoda i razlikuje se po jeziku i po tome da li je termin
      potvrđen ili je zahtev tek poslat. Sa nowrap je engleski naslov izlazio iz
      kartice i pravio horizontalni skrol na telefonu.
    */
    text-wrap: balance;
    animation: ${fadeUp} 420ms 120ms var(--ease-out) both;
  }
`;

const SuccessSubtitle = styled(Text)`
  &.ant-typography {
    color: var(--color-text);
    font-size: 1.0625rem;
    font-weight: var(--weight-medium);
    max-width: 360px;
    line-height: var(--leading-relaxed);
    animation: ${fadeUp} 420ms 200ms var(--ease-out) both;
  }
`;

const SuccessHelper = styled(Text)`
  &.ant-typography {
    color: var(--color-text-tertiary);
    font-size: 0.8125rem;
    max-width: 360px;
    line-height: var(--leading-normal);
    margin-top: 6px;
    animation: ${fadeUp} 420ms 260ms var(--ease-out) both;
  }
`;

const SuccessPrimaryAction = styled(Button)`
  &.ant-btn {
    margin-top: 24px;
    height: 44px;
    padding: 0 28px;
    border-radius: var(--radius-md);
    font-weight: var(--weight-semibold);
    animation: ${fadeUp} 420ms 340ms var(--ease-out) both;
  }
`;

const SuccessSecondaryLink = styled(Link)`
  margin-top: 16px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--color-text-secondary);
  font-size: 0.875rem;
  font-weight: var(--weight-medium);
  text-decoration: none;
  animation: ${fadeUp} 420ms 420ms var(--ease-out) both;
  transition: color var(--duration-normal) var(--ease-out);

  .label {
    text-decoration: underline;
    text-decoration-color: var(--color-border);
    text-underline-offset: 4px;
    text-decoration-thickness: 1.5px;
    transition: text-decoration-color var(--duration-normal) var(--ease-out);
  }

  .arrow {
    transition: transform var(--duration-normal) var(--ease-out);
  }

  &:hover {
    color: var(--color-primary);

    .label {
      text-decoration-color: var(--color-primary);
    }

    .arrow {
      transform: translateX(3px);
    }
  }
`;

interface Action {
  label: string;
  /** Ili `to` (navigacija), ili `onClick`. */
  to?: string;
  onClick?: () => void;
}

interface Props {
  title: string;
  description: string;
  /** Sitniji pomoćni red ispod opisa. */
  helper?: string;
  primary?: Action;
  /** Tiši link ispod glavnog dugmeta. */
  secondary?: { label: string; to: string };
}

/**
 * Ekran uspeha posle slanja forme (rezervacija, prijava partnera). Jedan izgled
 * za sve, da potvrde ne izgledaju kao da su iz različitih aplikacija.
 */
export const SuccessCard: React.FC<Props> = ({
  title,
  description,
  helper,
  primary,
  secondary,
}) => {
  const navigate = useNavigate();

  useEffect(() => {
    // Sa dna duge forme `smooth` skrol traje i po sekund, pa bi se kartica
    // potvrde pojavila dok je stranica još u pola ekrana.
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  return (
    <SuccessSection justify="center">
      <Col xs={24} md={16} lg={12} xl={10}>
        <ShadowBoxCard>
          {/* Zamena ekrana nije navigacija, pa čitač ekrana ovako sazna ishod. */}
          <SuccessWrap role="status">
            <SuccessBadge aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path d="M5 12.5 L10 17.5 L19 7.5" />
              </svg>
            </SuccessBadge>
            <SuccessTitle level={2}>{title}</SuccessTitle>
            <SuccessSubtitle>{description}</SuccessSubtitle>
            {helper && <SuccessHelper>{helper}</SuccessHelper>}
            {primary && (
              <SuccessPrimaryAction
                type="primary"
                onClick={() =>
                  primary.to ? navigate(primary.to) : primary.onClick?.()
                }
              >
                {primary.label}
              </SuccessPrimaryAction>
            )}
            {secondary && (
              <SuccessSecondaryLink to={secondary.to}>
                <span className="label">{secondary.label}</span>
                <span className="arrow" aria-hidden="true">
                  →
                </span>
              </SuccessSecondaryLink>
            )}
          </SuccessWrap>
        </ShadowBoxCard>
      </Col>
    </SuccessSection>
  );
};
